import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { fetchMe } from "../services/authService";
import {
  fetchWallet,
  fetchWithdrawalQuote,
  submitDepositRequest,
  submitWithdrawalRequest,
} from "../services/paymentService";
import { fetchPortfolio, listProjects } from "../services/investmentService";
import PortfolioHoldingMetrics from "../components/PortfolioHoldingMetrics";
import InvestorPageHeader from "../components/investor/InvestorPageHeader";
import InvestorSectionHeading from "../components/investor/InvestorSectionHeading";
import WalletToolbarLabel from "../components/investor/WalletToolbarLabel";
import WalletBalanceDisplay from "../components/wallet/WalletBalanceDisplay";
import WalletTransactionsReport from "../components/wallet/WalletTransactionsReport";
import { useAuth } from "../store/AuthContext";
import {
  holdingBuyValueTotal,
  issuePricePerShare,
  portfolioHoldingPricePerShare,
  portfolioHoldingValueTotal,
  shareValueBuyPlusPnl,
} from "../utils/investmentPricing";
import {
  filterInvestorCatalogProjects,
  formatProjectCodeDisplay,
  getProjectLifecycleLabel,
  isCancelledProjectStatus,
  isCompletedProjectStatus,
} from "../utils/projectViews";
import { hasWithdrawalPreset, withdrawalPresetFromUser } from "../utils/withdrawalPreset";

function unwrapList(data) {
  if (Array.isArray(data)) return data;
  if (data?.results && Array.isArray(data.results)) return data.results;
  return [];
}

function inferDepositProofContentType(file) {
  const explicit = (file?.type || "").toLowerCase().trim();
  if (explicit === "image/jpg") return "image/jpeg";
  if (["image/jpeg", "image/png", "application/pdf"].includes(explicit)) return explicit;
  const name = (file?.name || "").toLowerCase();
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".pdf")) return "application/pdf";
  return "";
}

function computeWithdrawalFeeBdt(payout, feePct, feeFixed) {
  const p = Number(payout);
  if (!Number.isFinite(p) || p <= 0) return 0;
  const pct = Number(feePct) || 0;
  const fixed = Number(feeFixed) || 0;
  let variable = 0;
  if (pct > 0) variable = Math.round((p * pct) / 100 * 100) / 100;
  return Math.round((fixed + variable) * 100) / 100;
}

function maxWithdrawalPayoutBdt(balance, feePct, feeFixed) {
  if (balance == null || !Number.isFinite(balance) || balance <= 0) return 0;
  let lo = 0;
  let hi = balance;
  for (let i = 0; i < 48; i += 1) {
    const mid = (lo + hi) / 2;
    const total = mid + computeWithdrawalFeeBdt(mid, feePct, feeFixed);
    if (total <= balance + 1e-6) lo = mid;
    else hi = mid;
  }
  return Math.floor(lo * 100) / 100;
}

function formatPayoutInputValue(n) {
  if (n == null || !Number.isFinite(n) || n <= 0) return "";
  return (Math.floor(n * 100) / 100).toFixed(2);
}

function parsePayoutInput(raw) {
  const s = String(raw ?? "").trim().replace(/,/g, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function extractApiError(data, fallback) {
  if (!data) return fallback;
  if (typeof data === "string" && data.trim()) return data;
  if (data.detail) return data.detail;
  if (data.non_field_errors?.[0]) return data.non_field_errors[0];
  const firstKey = Object.keys(data).find((k) => Array.isArray(data[k]) && data[k].length);
  if (firstKey) return data[firstKey][0];
  return fallback;
}

const WALLET_RECENT_TXN_DAYS = 7;

function getInvestorStageLabel(project) {
  const status = project?.status;
  if (isCancelledProjectStatus(status)) return getProjectLifecycleLabel(status);
  if (isCompletedProjectStatus(status)) return getProjectLifecycleLabel(status);
  const total = Number(project?.total_shares || 0);
  const sold = Number(project?.shares_sold || 0);
  if (total > 0 && sold >= total) return "Funded";
  return getProjectLifecycleLabel(status);
}

export default function WalletPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState("");
  const [portfolio, setPortfolio] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [portfolioError, setPortfolioError] = useState("");
  const [projects, setProjects] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [depositForm, setDepositForm] = useState({
    amount: "",
    bank_channel: "",
    transaction_ref: "",
    transaction_date: "",
  });
  const [depositProofFile, setDepositProofFile] = useState(null);
  const [depositSubmitting, setDepositSubmitting] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    bank_account_name: "",
    bank_account_number: "",
    bank_name: "",
    routing_number: "",
  });
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  /** Server-computed fee breakdown (matches Super Admin platform settings). */
  const [withdrawQuote, setWithdrawQuote] = useState(null);
  const bdtFull = useMemo(
    () => new Intl.NumberFormat("en-BD", { style: "currency", currency: "BDT", maximumFractionDigits: 2 }),
    []
  );
  const shareFmt = useMemo(
    () => new Intl.NumberFormat("en-BD", { maximumFractionDigits: 2, minimumFractionDigits: 0 }),
    []
  );
  const formatShare = (n) => shareFmt.format(Number(n || 0));
  const formatBdt = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return bdtFull.format(n);
  };

  const recentWalletTransactions = useMemo(() => {
    const list = wallet?.transactions;
    if (!Array.isArray(list)) return [];
    const cutoffMs = Date.now() - WALLET_RECENT_TXN_DAYS * 24 * 60 * 60 * 1000;
    return list.filter((t) => {
      if (!t?.created_at) return false;
      const ts = new Date(t.created_at).getTime();
      return Number.isFinite(ts) && ts >= cutoffMs;
    });
  }, [wallet?.transactions]);

  const holdingsWithShares = useMemo(
    () => (portfolio || []).filter((h) => Number(h.shares_held || 0) > 0),
    [portfolio]
  );

  const loadWithdrawQuote = async (amountRaw = "") => {
    const params = {};
    const payout = parsePayoutInput(amountRaw);
    if (payout != null && payout > 0) params.amount = formatPayoutInputValue(payout);
    try {
      const { data } = await fetchWithdrawalQuote(params);
      setWithdrawQuote(data);
    } catch {
      setWithdrawQuote(null);
    }
  };

  const loadWallet = async () => {
    setWalletLoading(true);
    setWalletError("");
    try {
      const { data } = await fetchWallet();
      setWallet(data);
    } catch (err) {
      setWallet(null);
      setWalletError(err.response?.data?.detail || "Could not load your Main Wallet.");
    } finally {
      setWalletLoading(false);
    }
  };

  const loadPortfolio = async () => {
    setPortfolioLoading(true);
    setPortfolioError("");
    try {
      const { data } = await fetchPortfolio();
      setPortfolio(unwrapList(data));
    } catch (err) {
      setPortfolio([]);
      setPortfolioError(err.response?.data?.detail || "Could not load your buy portfolio.");
    } finally {
      setPortfolioLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const { data } = await listProjects();
      setProjects(filterInvestorCatalogProjects(unwrapList(data)));
    } catch {
      setProjects([]);
    }
  };

  const refreshAll = async () => {
    setError("");
    await Promise.all([loadWallet(), loadPortfolio(), loadProjects().catch(() => {})]);
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const minDeposit = wallet?.min_wallet_deposit_bdt != null ? String(wallet.min_wallet_deposit_bdt) : "100";
  const minWithdraw =
    wallet?.min_wallet_withdrawal_bdt != null ? String(wallet.min_wallet_withdrawal_bdt) : "500";
  const walletBalanceBdt =
    wallet?.balance != null && wallet.balance !== "" && !Number.isNaN(Number(wallet.balance))
      ? Number(wallet.balance)
      : null;
  const kycStatus = user?.kyc_status ?? user?.kycStatus ?? "NOT_SUBMITTED";
  const withdrawBlockedByKyc = kycStatus !== "APPROVED";
  const withdrawFeePct =
    Number(withdrawQuote?.wallet_withdrawal_fee_pct ?? wallet?.wallet_withdrawal_fee_pct ?? 0) || 0;
  const withdrawFeeFixed =
    Number(withdrawQuote?.wallet_withdrawal_fee_fixed_bdt ?? wallet?.wallet_withdrawal_fee_fixed_bdt ?? 0) || 0;
  const minWithdrawEffective = withdrawQuote?.min_wallet_withdrawal_bdt ?? minWithdraw;
  const minWithdrawNum = Number(minWithdrawEffective) || 500;
  const quoteBalance = withdrawQuote?.wallet_balance_bdt ?? wallet?.balance;
  const walletBalanceForWithdraw =
    quoteBalance != null && quoteBalance !== "" && !Number.isNaN(Number(quoteBalance))
      ? Number(quoteBalance)
      : walletBalanceBdt;
  const maxPayoutFromQuote =
    withdrawQuote?.max_payout_bdt != null && withdrawQuote.max_payout_bdt !== ""
      ? Number(withdrawQuote.max_payout_bdt)
      : null;
  const withdrawAmountMax =
    maxPayoutFromQuote != null && maxPayoutFromQuote >= minWithdrawNum ? maxPayoutFromQuote : undefined;
  const withdrawPayoutPreview = parsePayoutInput(withdrawForm.amount);
  const withdrawFeePreview =
    withdrawQuote?.fee_bdt != null && withdrawPayoutPreview != null && withdrawPayoutPreview > 0
      ? Number(withdrawQuote.fee_bdt)
      : null;
  const withdrawTotalPreview =
    withdrawQuote?.total_debit_bdt != null && withdrawPayoutPreview != null && withdrawPayoutPreview > 0
      ? Number(withdrawQuote.total_debit_bdt)
      : null;
  const withdrawPayoutExceedsMax = Boolean(withdrawQuote?.exceeds_max_payout);
  const withdrawTotalExceedsBalance = Boolean(withdrawQuote?.exceeds_wallet_balance);
  const withdrawBelowMin =
    withdrawPayoutPreview != null && withdrawPayoutPreview > 0 && withdrawPayoutPreview < minWithdrawNum;
  const withdrawSubmitBlocked =
    withdrawBlockedByKyc ||
    withdrawBelowMin ||
    withdrawPayoutExceedsMax ||
    withdrawTotalExceedsBalance;

  const closeDepositModal = () => {
    setDepositModalOpen(false);
    setDepositProofFile(null);
    setError("");
    setMessage("");
  };
  const closeWithdrawModal = () => {
    setWithdrawModalOpen(false);
    setWithdrawQuote(null);
    setError("");
  };

  useEffect(() => {
    if (!withdrawModalOpen) return undefined;
    const timer = window.setTimeout(() => {
      loadWithdrawQuote(withdrawForm.amount).catch(() => {});
    }, 200);
    return () => window.clearTimeout(timer);
  }, [withdrawModalOpen, withdrawForm.amount]);

  const openDepositModal = () => {
    setError("");
    setMessage("");
    setWithdrawModalOpen(false);
    setDepositModalOpen(true);
  };

  const openWithdrawModal = () => {
    setDepositModalOpen(false);
    setError("");
    setMessage("");
    setWithdrawModalOpen(true);
    setWithdrawForm({
      amount: "",
      ...withdrawalPresetFromUser(user),
    });
    loadWallet().catch(() => {});
    loadWithdrawQuote("").catch(() => {});
    fetchMe()
      .then((res) => {
        if (res?.data) {
          refreshUser(res.data);
          setWithdrawForm((prev) => ({
            ...prev,
            ...withdrawalPresetFromUser(res.data),
          }));
        }
      })
      .catch(() => {});
    if (withdrawBlockedByKyc) {
      setError(
        "Identity verification (KYC) must be approved before BDT withdrawals. Complete KYC from your profile, then return here."
      );
    }
  };

  useEffect(() => {
    const raw = (searchParams.get("deposit") || "").toLowerCase();
    if (!raw) return undefined;
    setError("");
    setMessage("");
    setWithdrawModalOpen(false);
    if (raw === "requests" || raw === "list") {
      navigate("/wallet/deposits", { replace: true });
      return undefined;
    }
    if (raw === "1" || raw === "new" || raw === "true" || raw === "yes") {
      setDepositModalOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("deposit");
    setSearchParams(next, { replace: true });
    return undefined;
  }, [searchParams, setSearchParams, navigate]);

  useEffect(() => {
    const raw = (searchParams.get("withdraw") || "").toLowerCase();
    if (!raw) return undefined;
    setError("");
    setMessage("");
    setDepositModalOpen(false);
    if (raw === "requests" || raw === "list") {
      navigate("/wallet/withdrawals", { replace: true });
      return undefined;
    }
    if (raw === "1" || raw === "new" || raw === "true" || raw === "yes") {
      openWithdrawModal();
    }
    const next = new URLSearchParams(searchParams);
    next.delete("withdraw");
    setSearchParams(next, { replace: true });
    return undefined;
  }, [searchParams, setSearchParams, navigate]);

  const anyModalOpen = depositModalOpen || withdrawModalOpen;
  useEffect(() => {
    if (!anyModalOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setDepositModalOpen(false);
        setWithdrawModalOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [anyModalOpen]);

  const onSubmitDeposit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (depositProofFile) {
      const ct = inferDepositProofContentType(depositProofFile);
      if (!ct) {
        setError("Proof file must be JPG/JPEG, PNG, or PDF.");
        return;
      }
      if (depositProofFile.size > 5 * 1024 * 1024) {
        setError("Proof file is too large (max 5 MB).");
        return;
      }
    }
    setDepositSubmitting(true);
    try {
      await submitDepositRequest(
        {
          amount: depositForm.amount,
          bank_channel: depositForm.bank_channel.trim(),
          transaction_ref: depositForm.transaction_ref.trim(),
          transaction_date: depositForm.transaction_date,
          screenshot_url: "",
        },
        depositProofFile
      );
      const submittedMsg = depositProofFile
        ? "Deposit request submitted with proof. An admin will review and credit your wallet when approved."
        : "Deposit request submitted. An admin will match your transaction reference and credit your wallet when approved.";
      setDepositForm({
        amount: "",
        bank_channel: "",
        transaction_ref: "",
        transaction_date: "",
      });
      setDepositProofFile(null);
      closeDepositModal();
      await loadWallet();
      navigate("/wallet/deposits", { state: { message: submittedMsg } });
    } catch (err) {
      const d = err.response?.data;
      const msg =
        (typeof d === "string" && d) ||
        d?.detail ||
        (d?.amount && (Array.isArray(d.amount) ? d.amount[0] : d.amount)) ||
        (d?.transaction_ref && (Array.isArray(d.transaction_ref) ? d.transaction_ref[0] : d.transaction_ref)) ||
        (d?.proof && (Array.isArray(d.proof) ? d.proof[0] : d.proof)) ||
        (d?.non_field_errors && d.non_field_errors[0]) ||
        "Could not submit deposit request.";
      setError(msg);
    } finally {
      setDepositSubmitting(false);
    }
  };

  const onSubmitWithdraw = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (withdrawBlockedByKyc) {
      setError(
        "Identity verification (KYC) must be approved before a BDT withdrawal. Log out and back in if you were just approved."
      );
      return;
    }
    const amountNum = parsePayoutInput(withdrawForm.amount);
    if (amountNum == null || amountNum <= 0) {
      setError("Enter a valid payout amount.");
      return;
    }
    if (amountNum < minWithdrawNum) {
      setError(`Minimum payout is BDT ${minWithdraw}.`);
      return;
    }
    if (withdrawAmountMax != null && amountNum > withdrawAmountMax + 0.005) {
      setError(`Maximum payout after fees is ${bdtFull.format(withdrawAmountMax)}.`);
      return;
    }
    if (withdrawTotalExceedsBalance || withdrawPayoutExceedsMax) {
      setError(
        withdrawQuote?.fee_bdt != null
          ? `Payout ${bdtFull.format(amountNum)} plus fee ${bdtFull.format(Number(withdrawQuote.fee_bdt))} exceeds your wallet balance or maximum payout.`
          : "Amount exceeds your available balance or maximum payout."
      );
      return;
    }
    try {
      await submitWithdrawalRequest({ ...withdrawForm, amount: formatPayoutInputValue(amountNum) });
      const submittedMsg =
        "Your withdrawal request has been submitted successfully. Please allow some time for it to be reviewed and approved by the Accounts Department.";
      setWithdrawForm({
        amount: "",
        bank_account_name: "",
        bank_account_number: "",
        bank_name: "",
        routing_number: "",
      });
      closeWithdrawModal();
      await loadWallet();
      navigate("/wallet/withdrawals", { state: { message: submittedMsg } });
    } catch (err) {
      const d = err.response?.data;
      setError(extractApiError(d, "Withdrawal request failed."));
    }
  };

  return (
    <div className="page investor-premium investor-panel wallet-page">
      <InvestorPageHeader
        icon="wallet"
        title="Wallet & overview"
        subtitle="Main wallet balance, cash movements, and share holdings."
      />
      {kycStatus !== "APPROVED" && (
        <div className="investor-alert investor-alert--warning">
          <strong>KYC required for buying shares &amp; withdrawals</strong>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            Approved identity verification is required before buying project shares and before BDT withdrawals. You can still deposit to your wallet.
          </p>
          <Link to="/kyc">Complete KYC →</Link>
        </div>
      )}

      <section className="wallet-section wallet-section--primary" aria-labelledby="wallet-main-heading">
        <div className="wallet-section__head">
          <div>
            <p className="wallet-section__eyebrow">Cash account</p>
            <InvestorSectionHeading icon="main-wallet" tag="h2" id="wallet-main-heading" className="wallet-section__title">
              Main Wallet
            </InvestorSectionHeading>
          </div>
          <div className="wallet-toolbar">
            <button
              type="button"
              className="wallet-toolbar__primary"
              disabled={walletLoading || !!walletError}
              onClick={() => openDepositModal()}
            >
              <WalletToolbarLabel icon="deposit">Deposit</WalletToolbarLabel>
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={walletLoading || !!walletError}
              onClick={() => openWithdrawModal()}
            >
              <WalletToolbarLabel icon="withdraw">Withdraw</WalletToolbarLabel>
            </button>
            <Link to="/wallet/deposits" className="btn-secondary">
              <WalletToolbarLabel icon="deposit">My deposits</WalletToolbarLabel>
            </Link>
            <Link to="/wallet/withdrawals" className="btn-secondary">
              <WalletToolbarLabel icon="withdraw">My withdrawals</WalletToolbarLabel>
            </Link>
            <Link to="/transactions" className="btn-secondary">
              <WalletToolbarLabel icon="wallet-activity">Wallet Activity</WalletToolbarLabel>
            </Link>
          </div>
        </div>
        {walletLoading && <p className="wallet-empty">Loading wallet…</p>}
          {walletError && <p className="msg msg-error" style={{ margin: 0 }}>{walletError}</p>}
          {!walletLoading && !walletError && wallet && (
            <>
            <div className="wallet-balance-block">
              <WalletBalanceDisplay value={wallet.balance} />
              <p className="wallet-balance-caption">Available balance</p>
              <details className="wallet-balance-details">
                <summary>How your balance works</summary>
                <p>
                  Deposits increase your balance; withdrawals reduce it. Share sale or redemption proceeds are credited when
                  settled. Bank deposits are manual transfer requests after admin approval. Share purchases appear in recent
                  movements as debits.
                </p>
              </details>
            </div>
            <div className="wallet-stat-grid wallet-stat-grid--summary">
              <div className="wallet-stat-card">
                <div className="wallet-stat-card__label">Bank deposits (approved)</div>
                <div className="wallet-stat-card__value" title={formatBdt(wallet.total_deposited)}>
                  {formatBdt(wallet.total_deposited)}
                </div>
              </div>
              <div className="wallet-stat-card">
                <div className="wallet-stat-card__label">Share purchases (total)</div>
                <div className="wallet-stat-card__value" title={formatBdt(wallet.total_share_purchases)}>
                  {formatBdt(wallet.total_share_purchases)}
                </div>
              </div>
              <div className="wallet-stat-card">
                <div className="wallet-stat-card__label">Total withdrawn</div>
                <div className="wallet-stat-card__value" title={formatBdt(wallet.total_withdrawn)}>
                  {formatBdt(wallet.total_withdrawn)}
                </div>
              </div>
            </div>
            <div className="wallet-movements">
              <div className="wallet-movements__head">
                <h4>Recent Wallet Transactions</h4>
                <Link to="/transactions" className="btn-secondary compact-btn">
                  View all
                </Link>
              </div>
              {recentWalletTransactions.length ? (
                <WalletTransactionsReport
                  transactions={recentWalletTransactions}
                  bdtFull={bdtFull}
                  periodDays={WALLET_RECENT_TXN_DAYS}
                />
              ) : wallet.transactions?.length ? (
                <p className="wallet-empty">
                  No transactions in the last {WALLET_RECENT_TXN_DAYS} days. Use View all for full history.
                </p>
              ) : (
                <p className="wallet-empty">No wallet transactions yet.</p>
              )}
            </div>
            </>
          )}
        {!walletLoading && !walletError && !wallet && (
          <p className="wallet-empty">Wallet data unavailable.</p>
        )}
      </section>

      <section className="wallet-section wallet-holdings-section" aria-labelledby="wallet-holdings-heading">
        <div className="wallet-section__head">
          <div>
            <p className="wallet-section__eyebrow">Equity</p>
            <InvestorSectionHeading icon="holdings" tag="h2" id="wallet-holdings-heading" className="wallet-section__title">
              Active share holdings
            </InvestorSectionHeading>
          </div>
          <div className="wallet-toolbar">
            <button type="button" className="btn-secondary" onClick={() => navigate("/investments")}>
              Buy / redeem
            </button>
            <button type="button" className="btn-secondary" disabled={portfolioLoading} onClick={() => loadPortfolio()}>
              Refresh
            </button>
          </div>
        </div>
        <p className="wallet-empty" style={{ marginTop: "-0.35rem" }}>
          Projects where you currently hold shares. Cost, NAV/share, and total value — same format as Shares Management.
        </p>
        {portfolioLoading && <p className="wallet-empty">Loading holdings…</p>}
        {portfolioError && <p className="msg msg-error" style={{ margin: 0 }}>{portfolioError}</p>}
        {!portfolioLoading && !portfolioError && (
          <>
            {!holdingsWithShares.length ? (
              <p className="wallet-empty">
                You have no shares on hand right now.{" "}
                <Link to="/investments">Browse projects</Link> to invest.
              </p>
            ) : (
              <div className="table-wrap">
                <table className="ops-table wallet-holding-metrics">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Holding</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {holdingsWithShares.map((row, idx) => {
                      const p = row.project || {};
                      const code = formatProjectCodeDisplay(p.project_code) || "—";
                      const name = p.name || "—";
                      const cost = bdtFull.format(holdingBuyValueTotal(row));
                      const nav = bdtFull.format(portfolioHoldingPricePerShare(p));
                      const value = bdtFull.format(portfolioHoldingValueTotal(row, p));
                      const sumTitle = `${code}: ${name} · ${formatShare(row.shares_held)} sh · Cost ${cost} · NAV ${nav} · Unrealised Value ${value}`;
                      return (
                        <tr key={row.id}>
                          <td>{idx + 1}</td>
                          <td style={{ maxWidth: 560 }}>
                            <PortfolioHoldingMetrics
                              project={p}
                              sharesHeld={row.shares_held}
                              codeDisplay={code}
                              projectName={name}
                              sharesFormatted={formatShare(row.shares_held)}
                              costFormatted={cost}
                              navFormatted={nav}
                              valueFormatted={value}
                              title={sumTitle}
                            />
                          </td>
                          <td><span className={`badge badge-${p.status || "PENDING"}`}>{getInvestorStageLabel(p)}</span></td>
                          <td>
                            <button
                              type="button"
                              className="btn-secondary compact-btn"
                              onClick={() => navigate(`/investments?project_id=${p.id}`)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <section className="wallet-section" aria-labelledby="wallet-catalog-heading">
        <div className="wallet-section__head">
          <div>
            <p className="wallet-section__eyebrow">Opportunities</p>
            <InvestorSectionHeading icon="browse-projects" tag="h2" id="wallet-catalog-heading" className="wallet-section__title">
              Browse projects
            </InvestorSectionHeading>
          </div>
          <div className="wallet-page-footer-actions">
            <button type="button" onClick={() => refreshAll()}>
              Refresh all
            </button>
            <button type="button" className="btn-secondary" onClick={() => loadProjects().catch(() => {})}>
              Refresh list
            </button>
          </div>
        </div>
        {projects.length ? (
          <ul className="wallet-catalog-grid">
            {projects.map((p) => (
              <li key={p.id}>
                <Link to={`/investments?project_id=${p.id}`} className="wallet-catalog-card">
                  <p className="wallet-catalog-card__name">{p.name}</p>
                  <div className="wallet-catalog-card__meta">
                    <span className={`badge badge-${p.status}`}>{p.status}</span>
                    <span>Listing {issuePricePerShare(p).toFixed(2)} BDT/sh</span>
                    <span className="wallet-catalog-card__nav">NAV {shareValueBuyPlusPnl(p).toFixed(2)} BDT/sh</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="wallet-empty">No projects available to browse right now.</p>
        )}
        {!depositModalOpen && message && <p className="msg msg-success">{message}</p>}
        {!depositModalOpen && error && <p className="msg msg-error">{error}</p>}
      </section>

      {withdrawModalOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeWithdrawModal();
          }}
        >
          <div
            className="modal-card modal-card-animate wallet-withdraw-modal"
            style={{ width: "min(720px, 100%)" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="wallet-withdraw-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-btn"
              type="button"
              onClick={closeWithdrawModal}
              aria-label="Close withdrawal"
            >
              ×
            </button>
            <InvestorSectionHeading
              icon="withdraw"
              tag="h3"
              id="wallet-withdraw-title"
              className="wallet-modal__title"
              style={{ marginRight: 36, marginBottom: 4 }}
            >
              BDT withdrawal center
            </InvestorSectionHeading>
            <p className="modal-sub" style={{ marginTop: 0 }}>
              Submit a new withdrawal request.{" "}
              <Link to="/wallet/withdrawals" onClick={closeWithdrawModal}>
                View my withdrawals
              </Link>{" "}
              on the full page.
            </p>
            <div className="modal-tab-row" role="tablist" aria-label="Withdrawal views">
              <button type="button" role="tab" aria-selected="true" className="modal-tab modal-tab--active">
                New request
              </button>
              <Link
                to="/wallet/withdrawals"
                role="tab"
                className="modal-tab"
                style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                onClick={closeWithdrawModal}
              >
                My withdrawals
              </Link>
            </div>

            <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>
              Minimum payout <strong>BDT {minWithdraw}</strong> (platform setting).
              {(withdrawFeePct > 0 || withdrawFeeFixed > 0) && (
                <>
                  {" "}
                  Fee:{" "}
                  {withdrawFeeFixed > 0 ? <strong>{bdtFull.format(withdrawFeeFixed)}</strong> : null}
                  {withdrawFeeFixed > 0 && withdrawFeePct > 0 ? " + " : null}
                  {withdrawFeePct > 0 ? (
                    <strong>
                      {withdrawFeePct}% of payout
                    </strong>
                  ) : null}{" "}
                  (added on top of payout; both are debited from your wallet when processed).
                </>
              )}{" "}
              <strong>Approved KYC is required</strong> for BDT withdrawals only.
            </p>
            <div
              className="card"
              style={{ padding: "12px 14px", marginBottom: 12, background: "var(--surface-alt)" }}
              aria-live="polite"
            >
              {walletLoading ? (
                <p className="muted" style={{ margin: 0, fontSize: 14 }}>
                  Loading available balance…
                </p>
              ) : walletError ? (
                <p className="msg msg-error" style={{ margin: 0, fontSize: 13 }}>
                  {walletError}
                </p>
              ) : walletBalanceForWithdraw != null ? (
                <>
                  <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                    Wallet balance
                  </p>
                  <WalletBalanceDisplay
                    value={walletBalanceForWithdraw}
                    className="wallet-balance-hero"
                    size="compact"
                    style={{ marginTop: 4 }}
                  />
                  <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>
                    Platform fee: <strong>{withdrawFeePct}%</strong>
                    {withdrawFeeFixed > 0 ? (
                      <>
                        {" "}
                        + <strong>{bdtFull.format(withdrawFeeFixed)}</strong> per request
                      </>
                    ) : null}
                    .
                    {withdrawAmountMax != null && (withdrawFeePct > 0 || withdrawFeeFixed > 0) ? (
                      <>
                        {" "}
                        Maximum <strong>payout to your bank</strong>:{" "}
                        <strong>{bdtFull.format(withdrawAmountMax)}</strong> (not the full wallet balance).
                      </>
                    ) : withdrawAmountMax != null ? (
                      <> Maximum payout: {bdtFull.format(withdrawAmountMax)}.</>
                    ) : null}
                  </p>
                </>
              ) : (
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                  Wallet balance unavailable.
                </p>
              )}
            </div>
            {withdrawBlockedByKyc ? (
              <p className="msg msg-error" style={{ margin: "0 0 8px" }} role="status">
                KYC status: <strong>{kycStatus}</strong>. Approve KYC in admin, then{" "}
                <button
                  type="button"
                  className="btn-secondary compact-btn"
                  style={{ display: "inline", verticalAlign: "baseline" }}
                  onClick={() => {
                    fetchMe()
                      .then((res) => {
                        if (res?.data) refreshUser(res.data);
                        setError("");
                      })
                      .catch(() => {});
                  }}
                >
                  refresh status
                </button>{" "}
                or <Link to="/kyc">complete KYC</Link>. Log out and back in if you were just approved.
              </p>
            ) : null}
            {error && <p className="msg msg-error" style={{ margin: 0 }}>{error}</p>}
            <form className="stack wallet-withdraw-form" onSubmit={onSubmitWithdraw}>
              <label>
                Payout to your bank (BDT)
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    required
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    style={{ flex: "1 1 200px" }}
                    placeholder={
                      withdrawAmountMax != null
                        ? `Min ${minWithdraw} · max ${bdtFull.format(withdrawAmountMax)}`
                        : `Minimum ${minWithdraw}`
                    }
                    value={withdrawForm.amount}
                    onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                    onBlur={() => {
                      const n = parsePayoutInput(withdrawForm.amount);
                      if (n == null) return;
                      let capped = n;
                      if (withdrawAmountMax != null && capped > withdrawAmountMax) capped = withdrawAmountMax;
                      setWithdrawForm((prev) => ({
                        ...prev,
                        amount: formatPayoutInputValue(capped),
                      }));
                    }}
                  />
                  {withdrawAmountMax != null && withdrawAmountMax >= minWithdrawNum ? (
                    <button
                      type="button"
                      className="btn-secondary compact-btn"
                      onClick={() =>
                        setWithdrawForm((prev) => ({
                          ...prev,
                          amount: formatPayoutInputValue(withdrawAmountMax),
                        }))
                      }
                    >
                      Use max payout
                    </button>
                  ) : null}
                </div>
                {withdrawPayoutPreview != null && withdrawPayoutPreview > 0 && withdrawFeePreview != null ? (
                  <small
                    className={withdrawTotalExceedsBalance || withdrawPayoutExceedsMax ? "msg msg-error" : "muted"}
                    style={{ display: "block", marginTop: 4, fontSize: 12, padding: withdrawTotalExceedsBalance || withdrawPayoutExceedsMax ? "6px 8px" : 0 }}
                    role={withdrawTotalExceedsBalance || withdrawPayoutExceedsMax ? "alert" : undefined}
                  >
                    To bank: <strong>{bdtFull.format(withdrawPayoutPreview)}</strong>
                    {" · "}
                    Fee: <strong>{bdtFull.format(withdrawFeePreview)}</strong>
                    {withdrawQuote?.fee_fixed_bdt != null || withdrawQuote?.fee_variable_bdt != null ? (
                      <>
                        {" "}
                        (
                        {Number(withdrawQuote?.fee_fixed_bdt) > 0
                          ? `fixed ${bdtFull.format(Number(withdrawQuote.fee_fixed_bdt))}`
                          : null}
                        {Number(withdrawQuote?.fee_fixed_bdt) > 0 && Number(withdrawQuote?.fee_variable_bdt) > 0
                          ? " + "
                          : null}
                        {Number(withdrawQuote?.fee_variable_bdt) > 0
                          ? `${withdrawFeePct}% of payout = ${bdtFull.format(Number(withdrawQuote.fee_variable_bdt))}`
                          : null}
                        )
                      </>
                    ) : null}
                    {" · "}
                    Debited from wallet: <strong>{bdtFull.format(withdrawTotalPreview)}</strong>
                    {withdrawTotalExceedsBalance ? (
                      <> — exceeds wallet balance ({bdtFull.format(walletBalanceForWithdraw)}).</>
                    ) : null}
                    {!withdrawTotalExceedsBalance && withdrawPayoutExceedsMax ? (
                      <> — above maximum payout ({bdtFull.format(withdrawAmountMax)}).</>
                    ) : null}
                  </small>
                ) : null}
              </label>
              {hasWithdrawalPreset(user) ? (
                <p className="muted" style={{ margin: "0 0 8px", fontSize: 12 }}>
                  Bank details from your{" "}
                  <Link to="/profile?section=withdrawal-preset" onClick={closeWithdrawModal}>
                    withdrawal preset
                  </Link>
                  . You can edit them for this request only.
                </p>
              ) : (
                <p className="muted" style={{ margin: "0 0 8px", fontSize: 12 }}>
                  <Link to="/profile?section=withdrawal-preset" onClick={closeWithdrawModal}>
                    Save a withdrawal preset
                  </Link>{" "}
                  to auto-fill account details next time.
                </p>
              )}
              <label>
                Account name
                <input
                  required
                  value={withdrawForm.bank_account_name}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_account_name: e.target.value })}
                />
              </label>
              <label>
                Account number
                <input
                  required
                  value={withdrawForm.bank_account_number}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_account_number: e.target.value })}
                />
              </label>
              <label>
                Bank name
                <input
                  required
                  value={withdrawForm.bank_name}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_name: e.target.value })}
                />
              </label>
              <label>
                Routing / branch (optional)
                <input
                  value={withdrawForm.routing_number}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, routing_number: e.target.value })}
                />
              </label>
              <div className="wallet-withdraw-form-actions">
                <button type="button" className="btn-secondary" onClick={closeWithdrawModal}>
                  Cancel
                </button>
                <button type="submit" disabled={withdrawSubmitBlocked}>
                  {withdrawBlockedByKyc
                    ? "Submit (KYC required)"
                    : withdrawTotalExceedsBalance || withdrawPayoutExceedsMax
                      ? "Submit (amount too high)"
                      : withdrawBelowMin
                        ? "Submit (below minimum)"
                        : "Submit withdrawal"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {depositModalOpen && (
        <div
          className="modal-backdrop modal-backdrop-investor"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDepositModal();
          }}
        >
          <div
            className="modal-card modal-card-animate modal-card-elevated investor-deposit-modal"
            style={{ maxHeight: "90vh", overflow: "auto" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="wallet-deposit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-btn" type="button" onClick={closeDepositModal} aria-label="Close deposit">
              ×
            </button>
            <InvestorSectionHeading
              icon="deposit"
              tag="h3"
              id="wallet-deposit-title"
              className="wallet-modal__title"
              style={{ marginRight: 36, marginBottom: 4 }}
            >
              New deposit request
            </InvestorSectionHeading>
            <p className="modal-sub" style={{ marginTop: 0 }}>
              Manual deposit: send BDT to the platform, then submit amount, channel, transaction ID, and optional proof
              file. An admin matches your request and credits your wallet.{" "}
              <Link to="/wallet/deposits" onClick={closeDepositModal}>
                View my deposit requests
              </Link>{" "}
              in full page.
            </p>
            <div className="modal-tab-row" role="tablist" aria-label="Deposit views">
              <button type="button" role="tab" aria-selected="true" className="modal-tab modal-tab--active">
                New request
              </button>
              <Link
                to="/wallet/deposits"
                role="tab"
                className="modal-tab"
                style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                onClick={closeDepositModal}
              >
                My deposit requests
              </Link>
            </div>

            {message && (
              <p className="msg msg-success" style={{ margin: 0 }}>
                {message}
              </p>
            )}
            {error && (
              <p className="msg msg-error" style={{ margin: 0 }}>
                {error}
              </p>
            )}

            <form className="stack" onSubmit={onSubmitDeposit}>
                <div className="card" style={{ padding: 12, background: "var(--surface-alt)", fontSize: 14 }}>
                  <strong>How it works</strong>
                  <ol className="muted" style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    <li>Send BDT to the platform account (bank transfer or mobile banking).</li>
                    <li>Copy the transaction ID / reference from your SMS or receipt.</li>
                    <li>Submit this form. You may attach a JPG/PNG/PDF proof (optional, stored on our server).</li>
                    <li>After admin approval, your wallet balance increases.</li>
                  </ol>
                </div>
                <label>
                  Amount (BDT)
                  <input required type="number" min={minDeposit} step="0.01" placeholder={`Minimum ${minDeposit}`} value={depositForm.amount} onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })} />
                </label>
                <label>
                  Bank or channel
                  <input required placeholder="e.g. bKash, Nagad, BRAC Bank" value={depositForm.bank_channel} onChange={(e) => setDepositForm({ ...depositForm, bank_channel: e.target.value })} />
                </label>
                <label>
                  Transaction reference
                  <input required placeholder="Trx ID from SMS or bank slip" value={depositForm.transaction_ref} onChange={(e) => setDepositForm({ ...depositForm, transaction_ref: e.target.value })} />
                </label>
                <label>
                  Transaction date
                  <input required type="date" max={new Date().toISOString().slice(0, 10)} value={depositForm.transaction_date} onChange={(e) => setDepositForm({ ...depositForm, transaction_date: e.target.value })} />
                </label>
                <div className="stack" style={{ gap: 8 }}>
                  <label htmlFor="deposit-proof-file">
                    Proof file (optional) — JPG, PNG, or PDF up to 5 MB
                  </label>
                  <input
                    id="deposit-proof-file"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    onChange={(e) => setDepositProofFile(e.target.files?.[0] || null)}
                    disabled={depositSubmitting}
                  />
                  {depositProofFile ? (
                    <span className="muted" style={{ fontSize: 12 }}>
                      Selected: {depositProofFile.name}
                    </span>
                  ) : null}
                </div>
                <button type="submit" disabled={depositSubmitting}>
                  {depositSubmitting ? "Submitting…" : "Submit deposit request"}
                </button>
              </form>
          </div>
        </div>
      )}
    </div>
  );
}
